import SharedDialog from '../../shared/shared-dialog';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Doc, GPanelPermissionsModel, PermissionsModel } from '../../../types';
import {
  UserKeys,
  deleteDocumentPermissions,
  updateDocumentPermissions,
} from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import ChangeLogDialog from '../../shared/change-log-dialog';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { Visibility } from './drive-file-edit-permissions-dialog';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Public from '@mui/icons-material/Public';
import Lock from '@mui/icons-material/Lock';
import Domain from '@mui/icons-material/Domain';

interface DriveFileVisibilityDialogProps {
  open: boolean;
  handleClose: () => void;
  visibility: Visibility;
  primaryDomain: string;
  selectedDoc: Doc;
  permission: '' | GPanelPermissionsModel | undefined;
  changeLogRoles: string[];
  ownerEmail: string;
  onVisibilityUpdated?: (
    docId: string,
    foundUnder: string,
    updatedPermission: PermissionsModel | null
  ) => void;
}

export default function DriveFileVisibilityDialog({
  open,
  handleClose,
  visibility,
  primaryDomain,
  selectedDoc,
  permission,
  changeLogRoles,
  ownerEmail,
  onVisibilityUpdated,
}: DriveFileVisibilityDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const getVisibilityType = (
    selectedVisibility: string
  ): 'anyone' | 'domain' | 'user' => {
    if (selectedVisibility.includes('Public')) return 'anyone';
    if (selectedVisibility.includes('Domain')) return 'domain';
    return 'user';
  };

  const getRoleType = (
    role?: string
  ):
    | 'reader'
    | 'writer'
    | 'owner'
    | 'commenter'
    | 'organizer'
    | 'fileOrganizer' => {
    switch (role) {
      case 'owner':
      case 'organizer':
      case 'fileOrganizer':
      case 'writer':
      case 'commenter':
      case 'reader':
        return role;
      default:
        return 'reader';
    }
  };

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (formData: { selectedVisibility: string; role: string }) => {
      const { selectedVisibility } = formData;

      if (selectedVisibility === 'Private') {
        return deleteDocumentPermissions(
          selectedDoc.id,
          ownerEmail,
          permission !== '' ? permission?.permId || '' : '',
          selectedDoc.title,
          changeLogMessage
        );
      } else {
        interface PermIdType {
          [permId: string]: string;
        }

        const permId: PermIdType = {};

        if (permission !== '' && permission?.permId) {
          permId['permId'] = permission?.permId;
        }

        return updateDocumentPermissions(
          selectedDoc.id,
          ownerEmail,
          selectedDoc.title,
          changeLogMessage,
          {
            ...permId,
            userEmail: selectedVisibility.includes('Domain')
              ? primaryDomain
              : null,
            role: getRoleType(formData.role),
            withLink: selectedVisibility.includes('With_Link'),
            type: selectedVisibility.includes('Public')
              ? 'anyone'
              : selectedVisibility.includes('Domain')
              ? 'domain'
              : 'user',
            allowFileDiscovery: selectedVisibility === 'Domain',
          }
        );
      }
    },

    onSuccess: (_data, variables) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Share permissions updated successfully!',
      });

      if (onVisibilityUpdated) {
        if (variables.selectedVisibility === 'Private') {
          onVisibilityUpdated(selectedDoc.id, selectedDoc.foundUnder, null);
        } else {
          const defaultPermission: PermissionsModel = {
            permId: '',
            userEmail: null,
            displayName: null,
            role: 'reader',
            additionalRoles: [],
            type: 'user',
            withLink: false,
            isCommenter: false,
            photoLink: '',
            docId: selectedDoc.id,
            docOwner: ownerEmail,
            docTitle: selectedDoc.title,
            mimeType: selectedDoc.mimeType,
            isPrimaryOwner: false,
            toBeDeleted: false,
            sendNotification: false,
            transferOwnership: false,
            permExpirationTime: null,
            allowFileDiscovery: false,
            permissionDetails: [],
          };

          const newPermission: PermissionsModel = {
            ...defaultPermission,
            ...(permission !== null ? permission : {}),
            type: getVisibilityType(variables.selectedVisibility),
            role: getRoleType(variables.role),
            allowFileDiscovery: variables.selectedVisibility === 'Domain',
          };

          onVisibilityUpdated(
            selectedDoc.id,
            selectedDoc.foundUnder,
            newPermission
          );
        }
      }

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DrivePermissions],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveAcls],
      });

      // Only invalidate the relevant query based on item type
      const isFolder = selectedDoc.mimeType.includes('folder');
      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments],
        });
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating sharing permissions. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.update_drive_visibility, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<{
    role: string;
    selectedVisibility: string;
  }> = (formData) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanEditDocVisibility.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(formData);
    }
  };
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isDirty },
    getValues,
  } = useForm({
    values: {
      selectedVisibility: visibility.visibility,
      role: visibility.role,
    },
  });

  const { selectedVisibility: vis, role: formRole } = watch();

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Change Visibility"
      dialogContent={
        <Box component="form">
          <ListItemButton
            dense
            selected={vis === 'Private'}
            onClick={() =>
              setValue('selectedVisibility', 'Private', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Lock />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Restricted"
              secondary={`Only people listed can ${
                formRole === 'reader'
                  ? 'view'
                  : formRole === 'commenter'
                  ? 'comment'
                  : formRole === 'writer'
                  ? 'edit'
                  : 'view'
              }`}
            />
          </ListItemButton>
          <ListItemButton
            dense
            selected={vis === 'Domain'}
            onClick={() =>
              setValue('selectedVisibility', 'Domain', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Domain />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${primaryDomain}`}
              secondary={`Anyone at ${primaryDomain} can find in search results`}
            />
          </ListItemButton>
          <ListItemButton
            dense
            selected={vis === 'Domain_With_Link'}
            onClick={() =>
              setValue('selectedVisibility', 'Domain_With_Link', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Domain />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${primaryDomain}`}
              secondary={`Anyone at ${primaryDomain} with the link can ${
                formRole === 'reader'
                  ? 'view'
                  : formRole === 'commenter'
                  ? 'comment'
                  : formRole === 'writer'
                  ? 'edit'
                  : 'view'
              }`}
            />
          </ListItemButton>
          <ListItemButton
            dense
            selected={vis === 'Public_With_Link'}
            onClick={() =>
              setValue('selectedVisibility', 'Public_With_Link', {
                shouldDirty: true,
              })
            }
          >
            <ListItemAvatar>
              <Avatar>
                <Public />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Anyone with the link"
              secondary={`Anyone on the internet with the link can ${
                formRole === 'reader'
                  ? 'view'
                  : formRole === 'commenter'
                  ? 'comment'
                  : formRole === 'writer'
                  ? 'edit'
                  : 'view'
              }`}
            />
          </ListItemButton>

          {vis !== 'Private' ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Controller
                name={`role`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'role',
                    }}
                    InputLabelProps={{
                      htmlFor: 'role',
                    }}
                    select
                    defaultValue="reader"
                    label="Role"
                    size="small"
                    fullWidth
                  >
                    <MenuItem key="reader" value="reader">
                      Viewer
                    </MenuItem>
                    <MenuItem key="commenter" value="commenter">
                      Commenter
                    </MenuItem>
                    <MenuItem key="writer" value="writer">
                      Editor
                    </MenuItem>
                  </TextField>
                )}
              />
            </>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate({ ...getValues() });
            }}
            mutateLoading={isSharing}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        disabled: !isDirty,
        loading: isSharing,
      }}
    />
  );
}
